import { Button, Input, Popover } from 'antd';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useState } from 'react';
import FilterIcon from 'assets/svg/Filter';
import {
    TPO_PROMO_KPI_EMPTY,
    TPO_PROMO_KPI_SEARCH_INPUT,
    TPO_PROMO_KPI_SELECT_ALL,
    TPO_PROMO_KPI_SELECT_BTN,
    TPO_PROMO_KPI_SELECT_PANEL,
    tpoOptionTestId,
} from '../../../config/testIds';

interface KpiSelectDropdownProps {
    disabled: boolean;
    options: { label: string; value: string }[];
    value: string[];
    onChange: (selected: string[]) => void;
    /** Base id for the option rows. Defaults to the panel id. */
    testId?: string;
}

const KpiSelectDropdown = ({
    disabled,
    options,
    value,
    onChange,
    testId = TPO_PROMO_KPI_SELECT_PANEL,
}: KpiSelectDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [searchString, setSearchString] = useState('');

    const selectedSet = useMemo(() => new Set(value), [value]);

    const filteredOptions = useMemo(() => {
        if (!searchString.trim()) return options;
        const query = searchString.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(query));
    }, [options, searchString]);

    const handleToggle = useCallback(
        (optionValue: string) => {
            if (selectedSet.has(optionValue)) {
                onChange(value.filter((v) => v !== optionValue));
            } else {
                onChange([...value, optionValue]);
            }
        },
        [value, onChange, selectedSet],
    );

    const handleOpenChange = useCallback((newOpen: boolean) => {
        setOpen(newOpen);
        if (!newOpen) {
            setSearchString('');
        }
    }, []);

    const allFilteredSelected = useMemo(
        () => filteredOptions.length > 0 && filteredOptions.every((o) => selectedSet.has(o.value)),
        [filteredOptions, selectedSet],
    );

    const handleSelectAll = useCallback(() => {
        const newValues = Array.from(new Set([...value, ...filteredOptions.map((o) => o.value)]));
        onChange(newValues);
    }, [value, filteredOptions, onChange]);

    const handleUnselectAll = useCallback(() => {
        const filteredValues = new Set(filteredOptions.map((o) => o.value));
        onChange(value.filter((v) => !filteredValues.has(v)));
    }, [value, filteredOptions, onChange]);

    const dropdownContent = (
        <div className="w-72 flex flex-col" data-testid={TPO_PROMO_KPI_SELECT_PANEL}>
            <div className="pr-2 py-[0.31rem]">
                <Input
                    placeholder="Search KPI..."
                    prefix={<SearchOutlined className="text-[#878787] text-base" />}
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    allowClear
                    size="small"
                    bordered={false}
                    style={{ backgroundColor: '#FAFAFA', borderRadius: '4px', height: '2rem' }}
                    data-testid={TPO_PROMO_KPI_SEARCH_INPUT}
                />
            </div>

            {filteredOptions.length > 0 && (
                <div className="flex justify-end px-3 pb-1 border-b border-gray-100">
                    <button
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                        data-testid={TPO_PROMO_KPI_SELECT_ALL}
                        data-state-all-selected={allFilteredSelected ? 'true' : undefined}
                        onClick={allFilteredSelected ? handleUnselectAll : handleSelectAll}
                    >
                        {allFilteredSelected ? 'Deselect All' : 'Select All'}
                    </button>
                </div>
            )}

            <div className="max-h-60 overflow-y-auto">
                {filteredOptions.length === 0 ? (
                    <div
                        className="flex justify-center items-center py-8 text-gray-400 text-sm"
                        data-testid={TPO_PROMO_KPI_EMPTY}
                    >
                        No KPIs found
                    </div>
                ) : (
                    filteredOptions.map((option) => {
                        const isSelected = selectedSet.has(option.value);
                        return (
                            <div
                                key={option.value}
                                className={`flex items-center justify-between px-3 py-[0.31rem] my-1 cursor-pointer transition-colors ${
                                    isSelected ? 'bg-[#F7F5FF]' : 'hover:bg-gray-50'
                                }`}
                                data-testid={tpoOptionTestId(testId, option.value)}
                                data-state-selected={isSelected ? 'true' : undefined}
                                onClick={() => handleToggle(option.value)}
                            >
                                <span className="text-sm text-[#18181B]">{option.label}</span>
                                {isSelected && <CheckOutlined className="text-indigo-600 text-base" />}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <Popover
            content={disabled ? null : dropdownContent}
            trigger="click"
            open={!disabled && open}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0 }}
        >
            <Button disabled={disabled} data-testid={TPO_PROMO_KPI_SELECT_BTN}>
                <div className="flex flex-row items-center gap-x-1">
                    <FilterIcon />
                    <p className="text-[#18181B] text-sm font-medium">KPI</p>
                </div>
            </Button>
        </Popover>
    );
};

KpiSelectDropdown.displayName = 'KpiSelectDropdown';

export default KpiSelectDropdown;
